import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export type AppRole = "admin" | "partner" | "buyer" | "manager" | "operator";

const withTimeout = async <T,>(promise: PromiseLike<T>, ms = 10_000): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error("role_request_timeout")), ms);
  });

  try {
    return await Promise.race([Promise.resolve(promise), timeout]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

export const useUserRole = () => {
  const { user, loading: authLoading } = useAuth();
  const [role, setRole] = useState<AppRole | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isPartner, setIsPartner] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [isOperator, setIsOperator] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [loading, setLoading] = useState(true);
  // true, если роль не удалось получить (сеть/таймаут) — НЕ путать с «нет прав»
  const [roleError, setRoleError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const retry = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    let cancelled = false;

    const resetState = () => {
      setRole(null);
      setIsAdmin(false);
      setIsSuperAdmin(false);
      setIsPartner(false);
      setIsManager(false);
      setIsOperator(false);
      setRequires2FA(false);
    };

    const fetchRole = async () => {
      if (authLoading) {
        setLoading(true);
        return;
      }

      if (!user) {
        resetState();
        setLoading(false);
        return;
      }

      setLoading(true);
      setRoleError(false);

      try {
        // Медленный/нестабильный интернет: до 3 попыток по 15 с, прежде чем считать это ошибкой
        let data: { role: string; is_super_admin: boolean | null }[] | null = null;
        let lastError: unknown = null;
        for (let attempt = 1; attempt <= 3; attempt++) {
          try {
            const res = await withTimeout(
              supabase
                .from("user_roles")
                .select("role, is_super_admin")
                .eq("user_id", user.id)
                .order("created_at", { ascending: true }),
              15_000,
            );
            if (res.error) throw res.error;
            data = res.data;
            lastError = null;
            break;
          } catch (e) {
            lastError = e;
            if (cancelled) return;
            if (attempt < 3) await new Promise((r) => setTimeout(r, 1500 * attempt));
          }
        }
        if (lastError) throw lastError;
        if (cancelled) return;

        if (data && data.length > 0) {
          const roles = data.map((r) => r.role as AppRole);
          const hasAdmin = roles.includes("admin");
          const hasManager = roles.includes("manager");
          const hasPartner = roles.includes("partner");
          const hasOperator = roles.includes("operator");
          const hasSuperAdmin = data.some((r) => r.role === "admin" && r.is_super_admin === true);

          setIsAdmin(hasAdmin);
          setIsSuperAdmin(hasSuperAdmin);
          setIsManager(hasManager || hasAdmin);
          setIsPartner(hasPartner || hasAdmin);
          setIsOperator(hasOperator || hasAdmin);

          if (hasAdmin) setRole("admin");
          else if (hasManager) setRole("manager");
          else if (hasOperator) setRole("operator");
          else if (hasPartner) setRole("partner");
          else setRole("buyer");

          // Сбой проверки 2FA не должен отнимать уже полученную роль
          try {
            const { data: twoFaData } = await withTimeout(
              supabase
                .from("user_2fa")
                .select("totp_enabled")
                .eq("user_id", user.id)
                .maybeSingle(),
              15_000,
            );
            if (cancelled) return;
            setRequires2FA(twoFaData?.totp_enabled ?? false);
          } catch (e) {
            console.warn("2FA status fetch failed:", e);
          }
        } else {
          resetState();
          setRole("buyer");
        }
      } catch (error) {
        console.error("Error fetching user role:", error);
        if (cancelled) return;
        // Не понижаем до покупателя: роль неизвестна, страницы покажут «Повторить»
        resetState();
        setRoleError(true);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchRole();

    return () => {
      cancelled = true;
    };
  }, [user?.id, authLoading, reloadKey]);

  return { role, isAdmin, isSuperAdmin, isPartner, isManager, isOperator, requires2FA, loading, roleError, retry };
};
